import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";

// JSONB matrix shape for species-paired breakdowns (locked key contract per Section 8)
export interface SpeciesKennelMatrix {
  dogs: { available: number; unavail: number; evaluation: number; strayWait: number; unknown: number };
  cats: { available: number; unavail: number; evaluation: number; strayWait: number; unknown: number };
  other: { available: number; unavail: number; evaluation: number; strayWait: number; unknown: number };
}

export interface SpeciesEvalMatrix {
  dogs: { complete: number; needsReview: number; needsRecs: number; unknown: number; error: number; null: number };
  cats: { complete: number; needsReview: number; needsRecs: number; unknown: number; error: number; null: number };
  other: { complete: number; needsReview: number; needsRecs: number; unknown: number; error: number; null: number };
}

// I15.1: Adoption level counts per species
export interface AdoptionLevelCounts {
  l1: number;
  l2: number;
  l3: number;
  l4: number;
  null: number;
}

export interface SpeciesAdoptionMatrix {
  dogs: AdoptionLevelCounts;
  cats: AdoptionLevelCounts;
  other: AdoptionLevelCounts;
}

export interface CapacitySnapshot {
  id: string;
  snapshotDate: string;
  scope: "system" | "location";
  locationCode: string | null;
  occupancyAllKennels: number;
  capacityWithLimits: number;
  occupancyWithLimits: number;
  utilizationPct: number | null;
  kennelsTotal: number;
  kennelsWithLimits: number;
  kennelsWithoutLimits: number;
  animalsInUnlimitedKennels: number;
  dataAsOf: string | null;
  staleLocationCount: number;
  currentLocationCount: number;
  noReportsLocationCount: number;
  createdAt: string;
  createdBy: string | null;
  source: "scheduler" | "manual";
  // I15 breakdown fields (species)
  occupancyDogs: number;
  occupancyCats: number;
  occupancyOther: number;
  // I15 breakdown fields (kennel status)
  kennelStatusAvailable: number;
  kennelStatusUnavail: number;
  kennelStatusEvaluation: number;
  kennelStatusStrayWait: number;
  kennelStatusUnknown: number;
  // I15 breakdown fields (eval status)
  evalStatusComplete: number;
  evalStatusNeedsReview: number;
  evalStatusNeedsRecs: number;
  evalStatusUnknown: number;
  evalStatusError: number;
  evalStatusNull: number;
  // I15 JSONB matrices (zero-filled, never null)
  speciesKennelMatrix: SpeciesKennelMatrix;
  speciesEvalMatrix: SpeciesEvalMatrix;
  // I15.1 adoption level breakdown fields
  adoptionLevelL1: number;
  adoptionLevelL2: number;
  adoptionLevelL3: number;
  adoptionLevelL4: number;
  adoptionLevelNull: number;
  // I15.1 JSONB matrix (zero-filled, never null)
  speciesAdoptionMatrix: SpeciesAdoptionMatrix;
}

export interface SystemSnapshotsResponse {
  snapshots: CapacitySnapshot[];
  count: number;
  days: number;
}

export interface LocationSnapshotsResponse {
  snapshots: CapacitySnapshot[];
  count: number;
  location: string;
  days: number;
}

export interface CapacityScheduleSettings {
  isEnabled: boolean;
  timezone: string;
  runTimeLocal: string;
  lastRunAt: string | null;
  lastRunSnapshotDate: string | null;
  lastError: string | null;
  updatedBy: string | null;
}

export interface ManualRunResult {
  ok: boolean;
  snapshotDate?: string;
  rowsWritten?: number;
  source?: string;
  noOp?: boolean;
  message?: string;
}

export interface HistoricalRebuildResult {
  ok: boolean;
  datesProcessed: number;
  rowsWritten: number;
  errors: Array<{ date: string; error: string }>;
  message: string;
}

export function useSystemSnapshots(days: number = 30, enabled: boolean = true) {
  return useQuery<SystemSnapshotsResponse>({
    queryKey: ["/api/snapshots/capacity/system", { days }],
    queryFn: async () => {
      const response = await fetch(`/api/snapshots/capacity/system?days=${days}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch system snapshots");
      }
      return response.json();
    },
    enabled,
  });
}

export function useLocationSnapshots(location: string | null, days: number = 30) {
  return useQuery<LocationSnapshotsResponse>({
    queryKey: ["/api/snapshots/capacity/location", { location, days }],
    queryFn: async () => {
      const response = await fetch(
        `/api/snapshots/capacity/location?location=${encodeURIComponent(location!)}&days=${days}`,
        { credentials: "include" }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch location snapshots");
      }
      return response.json();
    },
    enabled: !!location,
  });
}

// Hook for fetching multiple location snapshots in parallel
export function useMultiLocationSnapshots(locations: string[], days: number = 30) {
  // Clone and sort to create stable cache key without mutating input
  const sortedLocations = [...locations].sort();
  return useQuery<Record<string, LocationSnapshotsResponse>>({
    queryKey: ["/api/snapshots/capacity/multi-location", { locations: sortedLocations, days }],
    queryFn: async () => {
      if (locations.length === 0) return {};
      
      const results = await Promise.all(
        locations.map(async (location) => {
          const response = await fetch(
            `/api/snapshots/capacity/location?location=${encodeURIComponent(location)}&days=${days}`,
            { credentials: "include" }
          );
          if (!response.ok) {
            throw new Error(`Failed to fetch snapshots for ${location}`);
          }
          const data: LocationSnapshotsResponse = await response.json();
          return { location, data };
        })
      );
      
      return results.reduce((acc, { location, data }) => {
        acc[location] = data;
        return acc;
      }, {} as Record<string, LocationSnapshotsResponse>);
    },
    enabled: locations.length > 0,
  });
}

export function useCapacityScheduleSettings() {
  return useQuery<CapacityScheduleSettings>({
    queryKey: ["/api/admin/snapshots/capacity/settings"],
  });
}

export function useUpdateCapacityScheduleSettings() {
  return useMutation({
    mutationFn: async (data: { isEnabled?: boolean; runTimeLocal?: string }) => {
      return apiRequest("POST", "/api/admin/snapshots/capacity/settings", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/snapshots/capacity/settings"] });
    },
  });
}

export function useManualSnapshotRun() {
  return useMutation<ManualRunResult, Error, { reportDate?: string; force?: boolean }>({
    mutationFn: async ({ reportDate, force = false }) => {
      const params = new URLSearchParams();
      if (reportDate) params.set("report_date", reportDate);
      if (force) params.set("force", "true");
      const url = `/api/admin/snapshots/capacity/run${params.toString() ? "?" + params.toString() : ""}`;
      return apiRequest("POST", url, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/snapshots/capacity/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/snapshots/capacity/system"] });
      queryClient.invalidateQueries({ queryKey: ["/api/snapshots/capacity/location"] });
    },
  });
}

/**
 * Phase 6: Hook for rebuilding historical capacity snapshots.
 * Regenerates snapshots for the last N days.
 */
export function useHistoricalRebuild() {
  return useMutation<HistoricalRebuildResult, Error, { days: number }>({
    mutationFn: async ({ days }) => {
      return apiRequest("POST", "/api/admin/snapshots/capacity/rebuild", { days });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/snapshots/capacity/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/snapshots/capacity/system"] });
      queryClient.invalidateQueries({ queryKey: ["/api/snapshots/capacity/location"] });
      queryClient.invalidateQueries({ queryKey: ["/api/snapshots/capacity/multi-location"] });
    },
  });
}
